import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const STYLES = {
    title: { font: { bold: true, size: 18, color: { argb: "FF000080" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFDDEBF7" } }, alignment: { horizontal: "center", vertical: "middle" } },
    subtitle: { font: { bold: true, size: 14 }, alignment: { horizontal: "center", vertical: "middle" } },
    dateLine: { font: { italic: true, size: 11, color: { argb: "FF555555" } }, alignment: { horizontal: "left", vertical: "middle" } },
    header: { font: { bold: true, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } }, alignment: { vertical: "middle", horizontal: "center" } },
    data: { font: { bold: false }, alignment: { vertical: "middle", horizontal: "center" } },
    branchGroup: { font: { bold: true, size: 12, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } }, alignment: { vertical: "middle", horizontal: "left" } },
    productGroup: { font: { bold: true, size: 11, color: { argb: "FF1F4E79" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } }, alignment: { vertical: "middle", horizontal: "left" } },
    subtotal: { font: { bold: true, color: { argb: "FF1F4E79" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2EFDA" } }, alignment: { vertical: "middle", horizontal: "center" } },
    grandTotal: { font: { bold: true, size: 12, color: { argb: "FFFFFFFF" } }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF375623" } }, alignment: { vertical: "middle", horizontal: "center" } },
    zebra: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } } },
};

const border = {
    top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" },
};

function applyStyle(cell, style) {
    if (style.font) cell.font = { ...cell.font, ...style.font };
    if (style.fill) cell.fill = style.fill;
    if (style.alignment) cell.alignment = style.alignment;
}

function addCell(worksheet, row, col, value, style, isAmount) {
    const cell = worksheet.getCell(row, col);
    cell.value = value;
    cell.border = border;
    applyStyle(cell, style);
    if (isAmount && typeof value === "number") cell.numFmt = "#,##0.00";
}

function formatDate(d) {
    if (!d) return "";
    const date = new Date(d);
    return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
}

async function generateLoanProgressReportExcel(apiData, statusLabel) {
    if (!apiData || apiData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Loan Progress Report");

    const columns = [
        { header: "Loan Number", key: "loanNumber", width: 22 },
        { header: "Loan Type", key: "loanType", width: 24 },
        { header: "Loan Date", key: "loanDate", width: 14 },
        { header: "Loan Amount", key: "loanAmount", width: 18 },
        { header: "Outstanding Amount", key: "outstandingAmount", width: 18 },
        { header: "Recovery Option", key: "recoveryOption", width: 18 },
        { header: "Status", key: "status", width: 18 },
    ];

    const headerKeys = columns.map(c => c.key);
    const amountColIndices = [4, 5]; // Loan Amount, Outstanding Amount

    columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

    // Title section
    ws.mergeCells(1, 1, 1, columns.length);
    const titleCell = ws.getCell("A1");
    titleCell.value = "Loan Progress Report";
    applyStyle(titleCell, STYLES.title);
    ws.getRow(1).height = 50;

    ws.mergeCells(2, 1, 2, columns.length);
    const subCell = ws.getCell("A2");
    subCell.value = `Status: ${statusLabel}`;
    applyStyle(subCell, STYLES.subtitle);
    ws.getRow(2).height = 30;

    ws.mergeCells(3, 1, 3, columns.length);
    const dateCell = ws.getCell("A3");
    dateCell.value = `Printed Date: ${new Date().toLocaleDateString("en-GB")}`;
    applyStyle(dateCell, STYLES.dateLine);

    ws.addRow([]);

    // Header row (row 5)
    const headerRow = ws.addRow(columns.map(c => c.header));
    headerRow.eachCell((cell) => { applyStyle(cell, STYLES.header); cell.border = border; });
    headerRow.height = 22;

    // Sort data
    const sorted = [...apiData].sort((a, b) => {
        const br = (a.branchName || "").localeCompare(b.branchName || "");
        if (br !== 0) return br;
        return (a.loanType || "").localeCompare(b.loanType || "");
    });

    // Grouped rows
    let currentRow = 6;
    let grandLoanAmt = 0, grandOutstanding = 0;

    let prevBranch = "";
    let branchLoanAmt = 0, branchOutstanding = 0;
    let productLoanAmt = 0, productOutstanding = 0;
    let prevProduct = "";

    sorted.forEach((item, idx) => {
        const branch = item.branchName || "Unknown";
        const product = item.loanType || "Unknown";

        if (idx > 0 && branch === prevBranch && product !== prevProduct) {
            if (productLoanAmt > 0) {
                ws.mergeCells(currentRow, 1, currentRow, 3);
                addCell(ws, currentRow, 1, `  Subtotal: ${prevProduct}`, STYLES.subtotal);
                addCell(ws, currentRow, 4, productLoanAmt, STYLES.subtotal, true);
                addCell(ws, currentRow, 5, productOutstanding, STYLES.subtotal, true);
                addCell(ws, currentRow, 6, "", STYLES.subtotal);
                addCell(ws, currentRow, 7, "", STYLES.subtotal);
                currentRow++;
            }
            productLoanAmt = 0;
            productOutstanding = 0;
        }

        if (idx > 0 && branch !== prevBranch) {
            if (productLoanAmt > 0) {
                ws.mergeCells(currentRow, 1, currentRow, 3);
                addCell(ws, currentRow, 1, `  Subtotal: ${prevProduct}`, STYLES.subtotal);
                addCell(ws, currentRow, 4, productLoanAmt, STYLES.subtotal, true);
                addCell(ws, currentRow, 5, productOutstanding, STYLES.subtotal, true);
                addCell(ws, currentRow, 6, "", STYLES.subtotal);
                addCell(ws, currentRow, 7, "", STYLES.subtotal);
                currentRow++;
            }

            ws.mergeCells(currentRow, 1, currentRow, 3);
            addCell(ws, currentRow, 1, `Branch Total: ${prevBranch}`, STYLES.subtotal);
            addCell(ws, currentRow, 4, branchLoanAmt, STYLES.subtotal, true);
            addCell(ws, currentRow, 5, branchOutstanding, STYLES.subtotal, true);
            addCell(ws, currentRow, 6, "", STYLES.subtotal);
            addCell(ws, currentRow, 7, "", STYLES.subtotal);
            currentRow++;

            branchLoanAmt = 0;
            branchOutstanding = 0;
            productLoanAmt = 0;
            productOutstanding = 0;
        }

        if (branch !== prevBranch) {
            ws.mergeCells(currentRow, 1, currentRow, columns.length);
            addCell(ws, currentRow, 1, `Branch: ${branch}`, STYLES.branchGroup);
            for (let c = 2; c <= columns.length; c++) addCell(ws, currentRow, c, "", STYLES.branchGroup);
            currentRow++;
            prevBranch = branch;
        }

        if (product !== prevProduct) {
            ws.mergeCells(currentRow, 1, currentRow, columns.length);
            addCell(ws, currentRow, 1, `  Product: ${product}`, STYLES.productGroup);
            for (let c = 2; c <= columns.length; c++) addCell(ws, currentRow, c, "", STYLES.productGroup);
            currentRow++;
            prevProduct = product;
        }

        const rla = Number(item.loanAmount) || 0;
        const rou = Number(item.outstandingAmount) || 0;

        const vals = [
            item.loanNumber || "",
            item.loanType || "",
            formatDate(item.loanDate),
            rla,
            rou,
            item.recoveryOption || "",
            item.status || "",
        ];

        const dataRow = ws.addRow(vals);
        dataRow.eachCell((cell, colNum) => {
            cell.border = border;
            applyStyle(cell, STYLES.data);
            if (amountColIndices.includes(colNum) && typeof cell.value === "number") cell.numFmt = "#,##0.00";
        });
        if (idx % 2 === 1) {
            dataRow.eachCell((cell) => { if (!cell.fill) cell.fill = STYLES.zebra.fill; });
        }
        dataRow.height = 20;

        branchLoanAmt += rla;
        branchOutstanding += rou;
        productLoanAmt += rla;
        productOutstanding += rou;
        grandLoanAmt += rla;
        grandOutstanding += rou;

        currentRow++;
    });

    // Last product subtotal
    if (productLoanAmt > 0) {
        ws.mergeCells(currentRow, 1, currentRow, 3);
        addCell(ws, currentRow, 1, `  Subtotal: ${prevProduct}`, STYLES.subtotal);
        addCell(ws, currentRow, 4, productLoanAmt, STYLES.subtotal, true);
        addCell(ws, currentRow, 5, productOutstanding, STYLES.subtotal, true);
        addCell(ws, currentRow, 6, "", STYLES.subtotal);
        addCell(ws, currentRow, 7, "", STYLES.subtotal);
        currentRow++;
    }

    // Last branch subtotal
    if (branchLoanAmt > 0) {
        ws.mergeCells(currentRow, 1, currentRow, 3);
        addCell(ws, currentRow, 1, `Branch Total: ${prevBranch}`, STYLES.subtotal);
        addCell(ws, currentRow, 4, branchLoanAmt, STYLES.subtotal, true);
        addCell(ws, currentRow, 5, branchOutstanding, STYLES.subtotal, true);
        addCell(ws, currentRow, 6, "", STYLES.subtotal);
        addCell(ws, currentRow, 7, "", STYLES.subtotal);
        currentRow++;
    }

    // Grand Total
    ws.mergeCells(currentRow, 1, currentRow, 3);
    addCell(ws, currentRow, 1, "GRAND TOTAL", STYLES.grandTotal);
    addCell(ws, currentRow, 4, grandLoanAmt, STYLES.grandTotal, true);
    addCell(ws, currentRow, 5, grandOutstanding, STYLES.grandTotal, true);
    addCell(ws, currentRow, 6, "", STYLES.grandTotal);
    addCell(ws, currentRow, 7, "", STYLES.grandTotal);
    currentRow += 2;

    // Summary section
    ws.mergeCells(currentRow, 1, currentRow, columns.length);
    addCell(ws, currentRow, 1, "Summary", { font: { bold: true, size: 14 } });
    currentRow++;

    const summaryItems = [
        ["Total Loan Amount", grandLoanAmt],
        ["Total Outstanding Balance", grandOutstanding],
        ["Recovery Rate (%)", grandLoanAmt > 0 ? ((grandLoanAmt - grandOutstanding) / grandLoanAmt * 100).toFixed(2) + "%" : "0%"],
        ["Total Records", sorted.length],
    ];

    summaryItems.forEach(([label, value]) => {
        addCell(ws, currentRow, 1, label, { font: { bold: true }, alignment: { vertical: "middle", horizontal: "right" } });
        ws.mergeCells(currentRow, 1, currentRow, 3);
        const valCell = ws.getCell(currentRow, 4);
        valCell.value = value;
        valCell.border = border;
        valCell.font = { bold: true, size: 12, color: { argb: "FF000080" } };
        valCell.numFmt = typeof value === "number" ? "#,##0.00" : undefined;
        valCell.alignment = { vertical: "middle", horizontal: "left" };
        ws.getRow(currentRow).height = 22;
        currentRow++;
    });

    // Status breakdown
    const statusBreakdown = {};
    sorted.forEach((item) => {
        const s = item.status || "Other";
        if (!statusBreakdown[s]) statusBreakdown[s] = { count: 0, loanAmount: 0, outstanding: 0 };
        statusBreakdown[s].count++;
        statusBreakdown[s].loanAmount += Number(item.loanAmount) || 0;
        statusBreakdown[s].outstanding += Number(item.outstandingAmount) || 0;
    });

    currentRow += 1;
    ws.mergeCells(currentRow, 1, currentRow, columns.length);
    addCell(ws, currentRow, 1, "Status Breakdown", { font: { bold: true, size: 14 } });
    currentRow++;

    const breakdownHeaders = ["Status", "No. of Loans", "Total Loan Amount", "Total Outstanding"];
    const breakdownHeaderRow = ws.addRow(breakdownHeaders);
    breakdownHeaderRow.eachCell((cell) => { applyStyle(cell, STYLES.header); cell.border = border; });
    currentRow++;

    const statusOrder = ["Active", "Non-Performing", "Closed", "Other"];
    statusOrder.forEach((s) => {
        const b = statusBreakdown[s];
        if (!b) return;
        const row = ws.addRow([s, b.count, b.loanAmount, b.outstanding]);
        row.eachCell((cell, colNum) => {
            cell.border = border;
            applyStyle(cell, STYLES.data);
            if (colNum >= 3 && typeof cell.value === "number") cell.numFmt = "#,##0.00";
        });
        currentRow++;
    });

    ws.addRow([]);
    currentRow++;

    // Freeze panes
    ws.views = [{ state: "frozen", xSplit: 1, ySplit: 5 }];

    // Export
    const buffer = await workbook.xlsx.writeBuffer();
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const finalFileName = `LoanProgressReport_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
    saveAs(new Blob([buffer]), finalFileName);
}

export default generateLoanProgressReportExcel;
